import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@apis/index";
import Routes from "@apis/routes";
import { NewCollectionResponse } from "@src/types/newCollection";
import { CollectionResponse } from "@src/types/CollectionListResponse";
import { FavoriteCount } from "@src/types/FavoriteCountResponse";
import { FavoriteListResponse } from "@src/types/FavoriteListResponse";
import { CollectionDetailListResponse } from "@src/types/CollectionDetailListResponse";
import { DiamondFavoriteResponse } from "@src/types/DiamondFavoriteResponse";
import { GemstoneFavoriteResponse } from "@src/types/GemstoneFavoriteResponse";
import { JewelryFavoriteResponse } from "@src/types/JewelryFavoriteResponse";
import { ProductCategory } from "@utils/constants";
import { getAddToFavoritesAPI } from "@apis/utils/search-results";
import { FavoritesEnum } from "@scenes/favorites/utils/FavoritesUtils";
import { useDispatch, useSelector } from "react-redux";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import {
  BulkActionsEnum,
  CartTypeEnum,
} from "@utils/product/searchResultConstants";
import { RootState } from "@redux/store";

export const useAddCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; type: number }) =>
      apiClient.post<NewCollectionResponse>(Routes.ADD_NEW_COLLECTION, data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [FavoritesEnum.full_list] }),
  });
};

export const useDiamondAddFavorite = () => {
  return useMutation({
    mutationFn: (data: { favorite: any }) =>
      apiClient.post<DiamondFavoriteResponse>(
        Routes.ADD_DIAMOND_FAVORITES,
        data
      ),
  });
};

export const useDiamondDeleteFavorite = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      apiClient.delete<DiamondFavoriteResponse>(
        Routes.DELETE_DIAMOND_FAVORITES(id)
      ),
  });
};

export const useGemstoneAddFavorite = () => {
  return useMutation({
    mutationFn: (data: { favorite: any }) =>
      apiClient.post<GemstoneFavoriteResponse>(
        Routes.ADD_GEMSTONE_FAVORITES,
        data
      ),
  });
};

export const useGemstoneDeleteFavorite = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      apiClient.delete<DiamondFavoriteResponse>(
        Routes.DELETE_ITEM_FAVORITES(id)
      ),
  });
};

export const useJewelryAddFavorite = () => {
  return useMutation({
    mutationFn: (data: { favorite: any }) =>
      apiClient.post<JewelryFavoriteResponse>(
        Routes.ADD_JEWELRY_FAVORITES,
        data
      ),
  });
};

export const useProductAddFavorites = (type: ProductCategory) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isGuest = useSelector((state: RootState) => state.userInfo.isGuest);
  return useMutation({
    mutationFn: (data: { favorite: any }) => {
      if (isGuest) {
        dispatch(setLoginModalType("default"));
        throw new Error("Guest user is not authorized");
      }
      return apiClient.post<JewelryFavoriteResponse>(
        getAddToFavoritesAPI(type),
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
      queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
    },
  });
};

export const useProductDeleteFavorites = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      apiClient.delete<DiamondFavoriteResponse>(
        Routes.DELETE_ITEM_FAVORITES(id)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
      queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
    },
  });
};

export const useJewelryDeleteFavorite = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) =>
      apiClient.delete<DiamondFavoriteResponse>(
        Routes.DELETE_ITEM_FAVORITES(id)
      ),
  });
};

export const useCollectionListModal = (
  page: number,
  pageSize: number,
  collectionType: string
) => {
  return useQuery({
    queryKey: ["COLLECTION_LIST_MODAL", page],
    enabled: page > 0,
    cacheTime: 0,
    queryFn: () =>
      apiClient.get<CollectionResponse>(Routes.GET_UNIFIED_COLLECTIONS, {
        params: {
          "collection[page]": page,
          "collection[per_page]": pageSize,
          "collection[type]": collectionType,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useCollectionList = (
  page: number,
  pageSize: number,
  collectionType: string,
  key = FavoritesEnum.full_list
) => {
  return useQuery({
    queryKey: [key, page, collectionType],
    enabled: page > 0,
    staleTime: 0,
    cacheTime: 0,
    queryFn: () =>
      apiClient.get<CollectionResponse>(Routes.GET_UNIFIED_COLLECTIONS, {
        params: {
          "collection[page]": page,
          "collection[per_page]": pageSize,
          "collection[type]": collectionType,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useFavoriteCount = (queryKey = ["FAVORITE_COUNT"]) => {
  return useQuery({
    queryKey,
    staleTime: 0,
    cacheTime: 0,
    queryFn: () =>
      apiClient.get<FavoriteCount>(Routes.GET_TOTAL_FAVORITES, {
        params: {
          type: "mix",
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useRenameCollection = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) =>
      apiClient.put(Routes.RENAME_COLLECTION(id), data),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: [FavoritesEnum.full_list] }),
  });
};

export const useDeleteCollection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: number }) =>
      apiClient.delete(Routes.DELETE_COLLECTION(data.id)),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [FavoritesEnum.full_list] }),
  });
};

export const useFavoritesList = (
  page: number,
  pageSize: number,
  type: string,
  priceMode: number
) => {
  return useQuery({
    queryKey: ["FAVORITES_LIST", page, pageSize, type, priceMode],
    enabled: page > 0,
    staleTime: 0,
    cacheTime: 0,
    queryFn: () =>
      apiClient.get<FavoriteListResponse>(Routes.GET_FAVORITES, {
        params: {
          "favorite[page]": page,
          "favorite[per_page]": pageSize,
          "favorite[type]": type,
          priceMode: priceMode,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useCollectionDetailList = (
  collectionId: number,
  mType: string,
  mPriceMode: number,
  page: number,
  pageSize: number
) => {
  return useQuery({
    queryKey: [
      "COLLECTION_DETAIL_LIST",
      collectionId,
      "initial",
      mType,
      mPriceMode,
      page,
      pageSize,
    ],
    enabled: collectionId > 0,
    staleTime: 0,
    retry: (_, error: any) => {
      const status = error?.response?.status;
      return !status || status >= 500;
    },
    queryFn: () =>
      apiClient.get<CollectionDetailListResponse>(
        Routes.GET_COLLECTIONS_DETAIL_LIST(collectionId),
        {
          params: {
            type: mType,
            priceMode: mPriceMode,
            page: page,
            per_page: pageSize,
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });
};

export const useCollectionDetailListWithPage = (
  collectionId: number,
  mType: string,
  mPriceMode: number,
  page: number,
  pageSize: number
) => {
  return useQuery({
    queryKey: [
      "COLLECTION_DETAIL_LIST",
      collectionId,
      "paged",
      mType,
      mPriceMode,
      page,
      pageSize,
    ],
    enabled: collectionId > 0 && page > 0 && pageSize > 0,
    staleTime: 0,
    queryFn: () =>
      apiClient.get<CollectionDetailListResponse>(
        Routes.GET_COLLECTIONS_DETAIL_LIST(collectionId),
        {
          params: {
            type: mType,
            priceMode: mPriceMode,
            page: page,
            per_page: pageSize,
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });
};

export const useAddItemsToCollection = (fromSearchResultScreen?: boolean) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { collection: any }) =>
      apiClient.post<any>(Routes.ADD_ITEMS_TO_COLLECTIONS, data),
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: ["COLLECTION_DETAIL_LIST"] })
        .then();
      if (fromSearchResultScreen) {
        queryClient.invalidateQueries({ queryKey: ["FAVORITES_LIST"] }).then();
        queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
      }
    },
  });
};

export const useBulkActions = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const isGuest = useSelector((state: RootState) => state.userInfo.isGuest);
  return useMutation({
    mutationFn: (data: {
      action_type: string;
      item_ids: Array<number>;
      price_mode: number;
      stock_type: string;
      product_sub_type?: string;
      cart_type?: string;
      item_cart_attributes?: Record<
        string,
        | {
            quantity: number;
            config: Record<string, string>;
            personalization?: Record<string, any>;
          }
        | Array<{
            quantity: number;
            config: Record<string, string>;
            personalization?: Record<string, any>;
          }>
      >;
    }) => {
      if (isGuest) {
        dispatch(setLoginModalType("default"));
        throw new Error("Guest user is not authorized");
      }
      return apiClient.post<any>(Routes.BULK_ACTIONS, data);
    },
    onSuccess: (data, variables) => {
      if (variables?.action_type) {
        if (
          [FavoritesEnum.add_favorites].includes(
            variables?.action_type as FavoritesEnum
          )
        ) {
          queryClient
            .invalidateQueries({ queryKey: ["FAVORITES_LIST"] })
            .then();
          queryClient
            .invalidateQueries({ queryKey: ["FAVORITE_COUNT"] })
            .then();
        } else if (
          [BulkActionsEnum.BULK_REQUEST_ACTION].includes(
            variables?.action_type as BulkActionsEnum
          )
        ) {
          if (
            variables?.cart_type === CartTypeEnum.ORDER_CART ||
            variables?.cart_type === CartTypeEnum.SHOPPING_CART_TYPE
          ) {
            queryClient
              .invalidateQueries({ queryKey: ["GET_CART_ITEM"] })
              .then();
          } else {
            queryClient
              .invalidateQueries({ queryKey: ["UNIFIED_BULK_REQUEST"] })
              .then();
          }
        }
      }
    },
  });
};

export const useRemoveItemFromCollection = () => {
  return useMutation({
    mutationFn: (data: { id: number; stock_item_ids: Array<number> }) =>
      apiClient.post<any>(Routes.REMOVE_ITEM_FROM_COLLECTION, data),
  });
};

export const useRemoveItemFromFavorites = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { favorite_ids: Array<number> }) =>
      apiClient.post<any>(Routes.REMOVE_ITEM_FROM_FAVORITES, data),
    onSuccess: () => {
      queryClient
        .invalidateQueries({ queryKey: [FavoritesEnum.full_list] })
        .then();
      queryClient.invalidateQueries({ queryKey: ["FAVORITE_COUNT"] }).then();
    },
  });
};
